import React from "react";
import { AtSign, Check, Clock } from "lucide-react";
import { Comment, CommentResolutionStatus } from "../types/prospect";
import { Button } from "./ui/button";

interface CommentListProps {
  comments: Comment[];
  onResolveComment?: (commentId: string) => void;
  currentUser?: string;
  relationshipManagers?: any[]; // Add this to access the RM list
}

export const CommentList: React.FC<CommentListProps> = ({ 
  comments, 
  onResolveComment,
  currentUser = "You",
  relationshipManagers = []
}) => {
  if (comments.length === 0) {
    return (
      <div className="text-center text-gray-500 py-6">
        Looks like you don't have any activities for conversations yet.
      </div>
    );
  }

  // Sort comments by date in descending order (newest first)
  // Sort comments by date in ascending order (oldest first)
 const sortedComments = [...comments].sort((a, b) => {
    if (a.id && b.id) {
      // If IDs are numeric strings, convert to numbers for comparison
      const aIdNum = parseInt(String(a.id));
      const bIdNum = parseInt(String(b.id));
      
      if (!isNaN(aIdNum) && !isNaN(bIdNum)) {
        return aIdNum - bIdNum;
      }
      
      // If IDs are not numeric, compare as strings
      return String(a.id).localeCompare(String(b.id));
    }
  return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  return (
    <div className="space-y-4 mb-6">
      {sortedComments.map((comment, index) => (
        <CommentItem 
          key={comment.id || index} 
          comment={comment} 
          onResolve={onResolveComment}
          currentUser={currentUser}
          relationshipManagers={relationshipManagers}
        />
      ))}
    </div>
  );
};

interface CommentItemProps {
  comment: Comment;
  onResolve?: (commentId: string) => void;
  currentUser: string;
  relationshipManagers: any[];
}

const CommentItem: React.FC<CommentItemProps> = ({ 
  comment, 
  onResolve, 
  currentUser,
  relationshipManagers
}) => {
  const getInitials = (name: string) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Helper function to get name from ID
  const getNameFromId = (id: string) => {
    // Check if id is already a name (string that's not a number)
    if (isNaN(Number(id))) return id;
    
    // Find the relationship manager with the matching ID
    const manager = relationshipManagers.find(rm => String(rm.id) === String(id));
    return manager ? manager.name : id; // Return name if found, otherwise return the ID
  };

  // Check if the current user is mentioned in the comment
  // const isUserMentioned = comment.mentions?.some(mention => {
  //   // If mention is an ID, compare with currentUser ID
  //   // If mention is a name, compare with currentUser name
  //   return mention === currentUser || getNameFromId(mention) === currentUser;
  // });

  

  const getCurrentUserId = () => {
    try {
      const userDetails = localStorage.getItem("detail");
      if (!userDetails) return null;
      
      const userData = JSON.parse(userDetails);
      return userData?.userId?.toString() || null;
    } catch (error) {
      console.error("Error parsing user details:", error);
      return null;
    }
  };

   const currentUserId = getCurrentUserId();
  const isUserMentioned = Array.isArray(comment.mentions) && 
    comment.mentions.some(mention => String(mention) === String(currentUserId));

  // Safely check resolution status
  const isPending = comment.resolutionStatus === 'pending' || !comment.resolutionStatus;
  const isResolved = comment.resolutionStatus === 'resolved';

  return (
    <div className="border rounded-md p-3">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 font-medium">
          {getInitials(comment.author)}
        </div>
        <div className="flex-grow">
          <p className="text-sm break-words whitespace-pre-wrap">
            {comment.icon === "phone" && <span className="mr-1">📞</span>}
            {comment.icon === "email" && <span className="mr-1">✉️</span>}
            {comment.text}
          </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-1">
            <span>{comment.author} on {formatDate(comment.date)}</span>
            
            {comment.mentions && comment.mentions.length > 0 && (
              <>
                <span>•</span>
                {comment.mentions.map((mention, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700"
                  >
                    <AtSign className="w-3 h-3 mr-1" />
                    {getNameFromId(mention)}
                  </span>
                ))}
              </>
            )}
          </div>
          {/* Resolution status and action button */}
            {Array.isArray(comment.mentions) && comment.mentions.length > 0 && (
            <div className="mt-2">
              {isUserMentioned && isPending && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-xs h-7 flex items-center gap-1"
                  onClick={() => onResolve && comment.id && onResolve(comment.id)}
                >
                  <Check className="w-3 h-3" /> Mark as resolved
                </Button>
              )}

              {(!isUserMentioned || isResolved) && (
                <div className={`flex items-center gap-1 text-xs ${
                  isResolved ? 'text-green-600' : 'text-amber-600'
                }`}>
                  {isResolved ? (
                    <>
                      <Check className="w-3 h-3" /> Resolved
                    </>
                  ) : (
                    <>
                      <Clock className="w-3 h-3" /> Pending
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentList;